import React, {
  useState, useEffect, useRef, useCallback, Component
} from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { endpoints } from '../api/config';

// ─── Constants ────────────────────────────────────────────────────────────────
const REFRESH_MS = 60000;
const HEALTH_API_URL = `${import.meta.env.VITE_API_URL}${endpoints.jyHealth}`;

const CLR = {
  healthy: '#059669', error: '#dc2626', warn: '#d97706',
  border: '#e6eaf1', text: '#0f172a', muted: '#64748b', subtle: '#94a3b8',
};

// ─── Date helpers ─────────────────────────────────────────────────────────────
// The API sends `timestamp` with a UTC offset, but `updated_at` values are
// timezone-less wall-clock strings written in US Eastern time. Parse those as
// Eastern, otherwise "x min ago" is off by 4-5 hours.
const NY_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  hourCycle: 'h23',
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit',
});

function nyOffsetMs(utcMs) {
  const p = {};
  NY_FMT.formatToParts(new Date(utcMs)).forEach((x) => { p[x.type] = x.value; });
  const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
  return asUtc - Math.floor(utcMs / 1000) * 1000;
}

function nyWallToDate(y, mo, d, h, mi, s) {
  const guess = Date.UTC(y, mo - 1, d, h, mi, s);
  let t = guess - nyOffsetMs(guess);
  t = guess - nyOffsetMs(t);
  return new Date(t);
}

function parseTs(str) {
  if (!str) return null;
  const s = String(str).trim();
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/i.test(s);
  if (hasTz) {
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
  if (!m) {
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }
  return nyWallToDate(+m[1], +m[2], +m[3], +m[4], +m[5], +m[6]);
}

function fmtDateTime(str) {
  try {
    const d = parseTs(str);
    if (!d) return str || '—';
    return d.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return str || '—';
  }
}

function relativeTime(str) {
  if (!str) return '—';
  try {
    const d = parseTs(str);
    if (!d) return str;
    const diffMs = Date.now() - d.getTime();
    if (diffMs < 45000) return 'just now';
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 60) return `${diffMin} min ago`;
    const diffH = Math.floor(diffMin / 60);
    const remMin = diffMin % 60;
    if (diffH < 24) return remMin > 0 ? `${diffH}h ${remMin}m ago` : `${diffH}h ago`;
    return `${Math.floor(diffH / 24)}d ago`;
  } catch {
    return str;
  }
}

function formatRunTime(sec) {
  if (typeof sec !== 'number' || isNaN(sec)) return '—';
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}m ${s}s`;
}

function getStatusTone(status) {
  if (!status) return 'gray';
  const s = String(status).toLowerCase().trim();
  if (['healthy', 'success', 'ok', 'available', 'running', 'passed', '200'].includes(s)) return 'green';
  if (['degraded', 'not configured', 'warning', 'warn', 'initializing'].includes(s)) return 'amber';
  if (['unhealthy', 'failed', 'impaired', 'alarm', 'error'].includes(s)) return 'red';
  return 'gray';
}

const num = (v) => (typeof v === 'number' && !isNaN(v) ? v : null);

// ─── Styles ───────────────────────────────────────────────────────────────────
const CSS = `
.sh-root {
  --ink: #0f172a; --muted: #64748b; --subtle: #94a3b8; --line: #e6eaf1;
  --surface: #ffffff; --soft: #f6f8fb; --navy: #1f3b63;
  --green: #059669; --green-bg: #ecfdf5; --green-line: #a7f3d0; --green-ink: #047857;
  --amber: #d97706; --amber-bg: #fffbeb; --amber-line: #fde68a; --amber-ink: #b45309;
  --red: #dc2626;   --red-bg: #fef2f2;   --red-line: #fecaca;   --red-ink: #b91c1c;
  --gray: #64748b;  --gray-bg: #f1f5f9; --gray-line: #cbd5e1;  --gray-ink: #475569;
  width: 100%; max-width: 1240px; margin: 0 auto; padding: 24px 24px 56px;
  box-sizing: border-box; display: flex; flex-direction: column; gap: 20px;
  color: var(--ink); font-variant-numeric: tabular-nums;
}
.sh-root *, .sh-root *::before, .sh-root *::after { box-sizing: border-box; }
.sh-root h1, .sh-root h2, .sh-root p { margin: 0; }
.sh-mono { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; }

/* tone helpers */
.sh-tone-green { --t: var(--green); --t-bg: var(--green-bg); --t-line: var(--green-line); --t-ink: var(--green-ink); }
.sh-tone-amber { --t: var(--amber); --t-bg: var(--amber-bg); --t-line: var(--amber-line); --t-ink: var(--amber-ink); }
.sh-tone-red   { --t: var(--red);   --t-bg: var(--red-bg);   --t-line: var(--red-line);   --t-ink: var(--red-ink); }
.sh-tone-gray  { --t: var(--gray);  --t-bg: var(--gray-bg);  --t-line: var(--gray-line);  --t-ink: var(--gray-ink); }

/* buttons */
.sh-btn {
  display: inline-flex; align-items: center; gap: 8px; height: 36px; padding: 0 14px;
  border-radius: 10px; border: 1px solid var(--line); background: var(--surface);
  color: var(--ink); font: inherit; font-size: 13px; font-weight: 600; cursor: pointer;
  transition: background .15s, border-color .15s;
}
.sh-btn:hover { background: var(--soft); border-color: var(--gray-line); }
.sh-btn:focus-visible, .sh-copy:focus-visible { outline: 2px solid var(--navy); outline-offset: 2px; }
.sh-btn:disabled { opacity: .65; cursor: default; }
.sh-btn--primary { background: var(--navy); border-color: var(--navy); color: #fff; }
.sh-btn--primary:hover { background: #17304f; border-color: #17304f; }
.sh-spin { animation: sh-rot .9s linear infinite; }
@keyframes sh-rot { to { transform: rotate(360deg); } }

/* hero: its whole tint follows the overall status */
.sh-hero {
  background: var(--t-bg); border: 1px solid var(--t-line); border-radius: 16px;
  padding: 22px 26px; display: flex; flex-wrap: wrap; align-items: center;
  justify-content: space-between; gap: 18px;
}
.sh-hero-main { display: flex; align-items: center; gap: 18px; min-width: 0; }
.sh-orb {
  position: relative; flex: none; width: 52px; height: 52px; border-radius: 50%;
  background: var(--t); color: #fff; display: grid; place-items: center;
}
.sh-orb::after {
  content: ''; position: absolute; inset: -6px; border-radius: 50%;
  border: 2px solid var(--t); opacity: .35; animation: sh-pulse 2.4s ease-out infinite;
}
@keyframes sh-pulse { 0% { transform: scale(.85); opacity: .5; } 100% { transform: scale(1.25); opacity: 0; } }
.sh-hero-kicker { font-size: 13px; font-weight: 600; color: var(--t-ink); }
.sh-hero h1 { font-size: 28px; line-height: 1.15; font-weight: 750; letter-spacing: -.02em; color: var(--ink); }
.sh-hero-sub { margin-top: 4px; font-size: 13px; color: var(--muted); }
.sh-hero-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.sh-auto { font-size: 12px; color: var(--muted); margin-right: 4px; }

/* overview strip: one joined card */
.sh-strip {
  display: grid; grid-template-columns: repeat(5, minmax(0, 1fr));
  background: var(--surface); border: 1px solid var(--line); border-radius: 14px; overflow: hidden;
}
.sh-kpi { padding: 18px 20px; display: flex; flex-direction: column; gap: 6px; border-right: 1px solid var(--line); min-width: 0; }
.sh-kpi:last-child { border-right: 0; }
.sh-kpi-top { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; color: var(--muted); }
.sh-kpi-ico { width: 28px; height: 28px; border-radius: 8px; background: var(--t-bg); color: var(--t); display: grid; place-items: center; }
.sh-kpi-val { font-size: 26px; font-weight: 750; letter-spacing: -.02em; line-height: 1.1; }
.sh-kpi-sub { font-size: 12px; color: var(--subtle); }
.sh-kpi-bar { height: 3px; border-radius: 3px; background: var(--t); margin-top: 6px; opacity: .85; }

/* cards */
.sh-card { background: var(--surface); border: 1px solid var(--line); border-radius: 14px; overflow: hidden; }
.sh-card-head {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 16px 22px; border-bottom: 1px solid var(--line);
}
.sh-card-title { display: flex; align-items: center; gap: 12px; min-width: 0; }
.sh-ico { width: 36px; height: 36px; border-radius: 10px; background: var(--soft); color: var(--navy); display: grid; place-items: center; flex: none; }
.sh-card-title h2 { font-size: 16px; font-weight: 700; letter-spacing: -.01em; }
.sh-card-title p { font-size: 12.5px; color: var(--muted); margin-top: 2px; }
.sh-card-body { padding: 18px 22px 22px; }
.sh-card-body--flush { padding: 0; }
.sh-cols { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; align-items: start; }

/* pills */
.sh-pill {
  display: inline-flex; align-items: center; gap: 6px; padding: 3px 10px; border-radius: 999px;
  background: var(--t-bg); color: var(--t-ink); border: 1px solid var(--t-line);
  font-size: 12px; font-weight: 600; text-transform: capitalize; white-space: nowrap;
}
.sh-pill i { width: 6px; height: 6px; border-radius: 50%; background: var(--t); }

/* tables */
.sh-scroll { overflow-x: auto; }
.sh-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.sh-table th {
  text-align: left; padding: 11px 22px; font-size: 12px; font-weight: 600; color: var(--muted);
  background: var(--soft); border-bottom: 1px solid var(--line); white-space: nowrap;
}
.sh-table td { padding: 14px 22px; border-bottom: 1px solid var(--line); vertical-align: middle; }
.sh-table tbody tr:last-child td { border-bottom: 0; }
.sh-table tbody tr:hover { background: #fafbfd; }
.sh-table tr.sh-row-bad, .sh-table tr.sh-row-bad:hover { background: var(--red-bg); }
.sh-name { font-weight: 650; }
.sh-id { font-size: 11.5px; color: var(--muted); margin-top: 2px; }
.sh-dim { color: var(--muted); }

/* bars */
.sh-bar { display: inline-flex; align-items: center; gap: 10px; }
.sh-track { width: 96px; height: 8px; border-radius: 999px; background: #e8edf4; overflow: hidden; flex: none; }
.sh-track--wide { width: 100%; }
.sh-fill { height: 100%; border-radius: 999px; background: var(--t); transition: width .25s ease; }
.sh-bar-label { font-size: 12.5px; font-weight: 650; min-width: 38px; }

/* tiles */
.sh-tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 12px; }
.sh-tile { background: var(--soft); border: 1px solid var(--line); border-radius: 12px; padding: 14px 16px; display: flex; flex-direction: column; gap: 8px; }
.sh-tile-label { font-size: 12.5px; font-weight: 600; color: var(--muted); }
.sh-tile-val { font-size: 24px; font-weight: 750; letter-spacing: -.02em; line-height: 1.1; }
.sh-tile-sub { font-size: 12px; color: var(--subtle); }

/* database block */
.sh-db { border: 1px solid var(--line); border-radius: 12px; padding: 16px; }
.sh-db + .sh-db { margin-top: 14px; }
.sh-db-head { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; margin-bottom: 14px; }
.sh-db-name { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; font-size: 15px; font-weight: 700; }
.sh-chip { font-size: 12px; font-weight: 600; background: var(--gray-bg); color: var(--gray-ink); padding: 3px 9px; border-radius: 6px; }
.sh-endpoint { display: flex; align-items: center; gap: 8px; min-width: 0; }
.sh-endpoint code {
  font-size: 12px; color: var(--muted); background: var(--soft); border: 1px solid var(--line);
  padding: 5px 10px; border-radius: 8px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 420px;
}
.sh-copy {
  height: 28px; padding: 0 10px; font: inherit; font-size: 12px; font-weight: 600; border-radius: 8px;
  border: 1px solid var(--line); background: var(--surface); color: var(--ink); cursor: pointer;
}
.sh-copy:hover { background: var(--soft); }
.sh-copy--done { background: var(--green-bg); border-color: var(--green-line); color: var(--green-ink); }

/* notes + alerts */
.sh-alert {
  display: flex; align-items: flex-start; gap: 12px; padding: 14px 16px; border-radius: 12px;
  background: var(--red-bg); border: 1px solid var(--red-line); color: var(--red-ink); font-size: 13.5px; font-weight: 550;
}
.sh-alert svg { flex: none; margin-top: 1px; }
.sh-note { display: flex; align-items: center; gap: 8px; padding: 12px 22px; font-size: 12.5px; color: var(--muted); background: var(--soft); border-top: 1px solid var(--line); }
.sh-note + .sh-note { border-top: 0; padding-top: 0; }
.sh-empty { font-size: 13px; color: var(--muted); padding: 6px 0; }
.sh-banner {
  display: flex; align-items: center; gap: 10px; padding: 10px 14px; border-radius: 10px;
  background: var(--amber-bg); border: 1px solid var(--amber-line); color: var(--amber-ink); font-size: 13px; font-weight: 550;
}

/* alarms */
.sh-alarm-item { display: flex; align-items: center; gap: 10px; padding: 10px 14px; border-radius: 10px; background: var(--red-bg); border: 1px solid var(--red-line); color: var(--red-ink); font-size: 13px; }
.sh-stack { display: flex; flex-direction: column; gap: 8px; margin-top: 14px; }

/* NetSuite endpoint cards */
.sh-ep-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
.sh-ep {
  display: flex; flex-direction: column; overflow: hidden; border-radius: 12px;
  background: var(--surface); border: 1px solid var(--line); border-top: 3px solid var(--t);
}
.sh-ep.sh-tone-red { background: var(--red-bg); border-color: var(--red-line); border-top-color: var(--red); }
.sh-ep-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 14px 18px; border-bottom: 1px solid var(--line); }
.sh-ep.sh-tone-red .sh-ep-head { border-bottom-color: var(--red-line); }
.sh-ep-title { font-size: 14px; font-weight: 700; min-width: 0; overflow-wrap: anywhere; }
.sh-ep-metrics { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; padding: 16px 18px 12px; }
.sh-ep-num { font-size: 28px; font-weight: 750; letter-spacing: -.02em; line-height: 1.1; color: var(--t-ink); }
.sh-ep-label { font-size: 13px; color: var(--muted); margin-top: 4px; }
.sh-ep-bar { padding: 0 18px 16px; }
.sh-ep-foot {
  display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 11px 18px;
  font-size: 12.5px; color: var(--muted); background: var(--soft); border-top: 1px solid var(--line); margin-top: auto;
}
.sh-ep.sh-tone-red .sh-ep-foot { background: transparent; border-top-color: var(--red-line); }
.sh-ep-foot strong { color: var(--ink); font-weight: 650; }

/* loading skeleton */
.sh-skel { background: linear-gradient(90deg, #eef2f7 25%, #f6f8fb 37%, #eef2f7 63%); background-size: 400% 100%; animation: sh-shim 1.4s ease infinite; border-radius: 8px; }
@keyframes sh-shim { 0% { background-position: 100% 50%; } 100% { background-position: 0 50%; } }

@media (max-width: 1080px) {
  .sh-strip { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .sh-kpi:nth-child(3) { border-right: 0; }
  .sh-kpi:nth-child(-n+3) { border-bottom: 1px solid var(--line); }
  .sh-cols { grid-template-columns: 1fr; }
  .sh-ep-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 680px) {
  .sh-root { padding: 16px 14px 40px; }
  .sh-strip { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .sh-ep-grid { grid-template-columns: 1fr; }
  .sh-kpi { border-right: 0 !important; border-bottom: 1px solid var(--line); }
  .sh-hero { padding: 18px; }
  .sh-hero h1 { font-size: 22px; }
  .sh-card-head, .sh-card-body { padding-left: 16px; padding-right: 16px; }
  .sh-table th, .sh-table td { padding-left: 14px; padding-right: 14px; }
}
@media (prefers-reduced-motion: reduce) {
  .sh-orb::after, .sh-spin, .sh-skel { animation: none; }
  .sh-fill { transition: none; }
}
`;

// ─── Icons (inline SVG, no extra dependency) ──────────────────────────────────
const ICONS = {
  server: 'M4 6h16v5H4zM4 13h16v5H4zM7.5 8.5h.01M7.5 15.5h.01',
  database: 'M4 6c0-1.7 3.6-3 8-3s8 1.3 8 3-3.6 3-8 3-8-1.3-8-3zM4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6',
  globe: 'M12 21a9 9 0 100-18 9 9 0 000 18zM3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9S14.5 18.4 12 21c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z',
  plug: 'M9 3v5M15 3v5M6 8h12v4a6 6 0 01-12 0zM12 18v3',
  sync: 'M20 11a8 8 0 00-14.5-3.5L4 9M4 4v5h5M4 13a8 8 0 0014.5 3.5L20 15M20 20v-5h-5',
  bell: 'M6 9a6 6 0 1112 0c0 6 2.5 7.5 2.5 7.5h-17S6 15 6 9zM10 20a2 2 0 004 0',
  back: 'M19 12H5M11 6l-6 6 6 6',
  refresh: 'M20 11a8 8 0 00-14.5-3.5L4 9M4 4v5h5M4 13a8 8 0 0014.5 3.5L20 15M20 20v-5h-5',
  check: 'M5 12.5l4.5 4.5L19 7.5',
  warn: 'M12 4l9.5 16.5h-19zM12 10v4.5M12 17.5h.01',
  cross: 'M6 6l12 12M18 6L6 18',
  info: 'M12 21a9 9 0 100-18 9 9 0 000 18zM12 11v5M12 8h.01',
};

const Icon = ({ name, size = 18, className }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true"
  >
    <path d={ICONS[name] || ICONS.info} />
  </svg>
);

// ─── Small components ─────────────────────────────────────────────────────────
const StatusPill = ({ status, label }) => (
  <span className={`sh-pill sh-tone-${getStatusTone(status || label)}`}>
    <i />
    {label || status || '—'}
  </span>
);

const Bar = ({ value, max = 100, tone = 'green', label, wide = false }) => {
  const n = num(value) ?? 0;
  const pct = Math.min(100, Math.max(0, max > 0 ? (n / max) * 100 : 0));
  return (
    <div className={`sh-bar sh-tone-${tone}`} style={wide ? { display: 'flex', width: '100%' } : undefined}>
      <div className={`sh-track${wide ? ' sh-track--wide' : ''}`}>
        <div className="sh-fill" style={{ width: `${pct}%` }} />
      </div>
      {label && <span className="sh-bar-label">{label}</span>}
    </div>
  );
};

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    if (!text) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch((err) => console.warn('Clipboard writeText failed:', err));
    } else {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (err) {
        console.warn('execCommand copy failed:', err);
      }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      type="button" onClick={handleCopy} aria-label="Copy endpoint"
      className={`sh-copy${copied ? ' sh-copy--done' : ''}`}
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

const Section = ({ icon, title, subtitle, status, flush, children }) => (
  <section className="sh-card">
    <div className="sh-card-head">
      <div className="sh-card-title">
        <span className="sh-ico"><Icon name={icon} /></span>
        <div>
          <h2>{title}</h2>
          {subtitle ? <p>{subtitle}</p> : null}
        </div>
      </div>
      {status !== undefined && <StatusPill status={status} />}
    </div>
    <div className={`sh-card-body${flush ? ' sh-card-body--flush' : ''}`}>{children}</div>
  </section>
);

const NotConfigured = ({ reason }) => (
  <div className="sh-alert" role="alert">
    <Icon name="warn" />
    <span>{reason}</span>
  </div>
);

const Tile = ({ label, value, sub, children }) => (
  <div className="sh-tile">
    <div className="sh-tile-label">{label}</div>
    {value !== undefined && <div className="sh-tile-val">{value}</div>}
    {children}
    {sub ? <div className="sh-tile-sub">{sub}</div> : null}
  </div>
);

// ─── Error Boundary ──────────────────────────────────────────────────────────
export class SystemHealthErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('SystemHealthPage caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    if (this.props.onRetry) this.props.onRetry();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 20px', background: '#fef2f2', border: `1px solid ${CLR.error}40`,
            borderRadius: 12, color: CLR.error, fontSize: 13, margin: '20px auto',
            maxWidth: 1200, width: '100%', boxSizing: 'border-box',
          }}
        >
          <span style={{ fontWeight: 600 }}>System health view encountered an error while loading.</span>
          <button
            type="button" onClick={this.handleRetry}
            style={{
              padding: '6px 14px', background: '#fff', border: `1px solid ${CLR.error}66`,
              borderRadius: 8, color: CLR.error, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── SystemHealthPage Content ────────────────────────────────────────────────
const SystemHealthContent = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [timestamp, setTimestamp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errored, setErrored] = useState(false);
  const abortRef = useRef(null);

  // Fetch health data
  const fetchHealth = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setRefreshing(true);
    try {
      const res = await fetch(HEALTH_API_URL, { signal: ctrl.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
      setTimestamp(json.timestamp || new Date().toISOString());
      setErrored(false);
    } catch (e) {
      if (e.name === 'AbortError') return;
      setErrored(true);
    } finally {
      if (abortRef.current === ctrl) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const iv = setInterval(fetchHealth, REFRESH_MS);
    return () => {
      clearInterval(iv);
      abortRef.current?.abort();
    };
  }, [fetchHealth]);

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/dashboard');
  };

  // Overall status
  const rawStatus = String(data?.status || '').toLowerCase();
  let overallHeadline = 'All systems healthy';
  let overallTone = 'green';
  if (['unhealthy', 'error', 'failed'].includes(rawStatus)) {
    overallHeadline = 'Systems unhealthy'; overallTone = 'red';
  } else if (['degraded', 'warn', 'warning'].includes(rawStatus)) {
    overallHeadline = 'Systems degraded'; overallTone = 'amber';
  } else if (['healthy', 'ok', 'success'].includes(rawStatus)) {
    overallHeadline = 'All systems healthy'; overallTone = 'green';
  } else if (data?.status) {
    overallHeadline = String(data.status); overallTone = getStatusTone(data.status);
  } else if (!data) {
    overallHeadline = loading ? 'Checking systems…' : 'Status unavailable';
    overallTone = 'gray';
  }
  const snapshotTs = data?.timestamp || timestamp;
  const snapshotLabel = snapshotTs ? `Snapshot taken ${fmtDateTime(snapshotTs)}` : 'Fetching snapshot…';

  // Safe extractions
  const ec2 = data?.ec2;
  const ec2Instances = Array.isArray(ec2?.instances) ? ec2.instances : [];
  const ec2RunningCount = ec2Instances.filter((i) => String(i?.state).toLowerCase() === 'running').length;
  const ec2TerminatedList = ec2Instances.filter((i) => String(i?.state).toLowerCase() === 'terminated');
  const isEc2NotConfigured = ec2Instances.length === 0 && Boolean(ec2?.reason);

  const rds = data?.rds;
  const rdsDatabases = Array.isArray(rds?.databases) ? rds.databases : [];
  const isRdsNotConfigured = rdsDatabases.length === 0 && Boolean(rds?.reason);

  const region = data?.region_health;
  const ns = data?.netsuite_endpoints;
  const epList = Array.isArray(ns?.endpoints) ? ns.endpoints : [];
  const maxExecTime = Math.max(...epList.map((e) => Number(e?.execution_time_seconds) || 0), 1);

  const sync = data?.priority_sync;
  const alarms = data?.alarms;
  const alarmSummary = alarms?.summary || alarms || {};
  const alarmOk = num(alarmSummary?.ok) ?? 0;
  const alarmFiring = num(alarms?.in_alarm_count) ?? num(alarmSummary?.alarm) ?? 0;
  const alarmInsufficient = num(alarmSummary?.insufficient_data) ?? 0;
  const alarmList = Array.isArray(alarms?.in_alarm) ? alarms.in_alarm : [];

  const kpis = data ? [
    {
      key: 'ec2', icon: 'server', label: 'EC2', status: ec2?.status,
      value: `${ec2?.running_instances ?? 0} of ${ec2?.total_instances ?? 0}`, sub: 'instances running',
    },
    {
      key: 'rds', icon: 'database', label: 'Database', status: rds?.status,
      value: `${rds?.available_databases ?? 0} of ${rds?.total_databases ?? 0}`, sub: 'available',
    },
    {
      key: 'region', icon: 'globe', label: 'Region', status: region?.status,
      value: num(region?.latency_ms) !== null ? `${Math.round(region.latency_ms).toLocaleString()} ms` : '—',
      sub: `${region?.region || 'us-east-1'}, HTTP ${region?.http_status ?? '—'}`,
    },
    {
      key: 'ns', icon: 'plug', label: 'NetSuite', status: ns?.status,
      value: ns?.summary || (ns?.total != null ? `${ns?.successful ?? 0}/${ns?.total}` : '—'), sub: 'endpoints passing',
    },
    {
      key: 'sync', icon: 'sync', label: 'Priority sync', status: sync?.status,
      value: num(sync?.not_synced) !== null ? sync.not_synced.toLocaleString() : '—', sub: 'waiting to sync',
    },
  ] : [];

  return (
    <div className="sh-root">
      <style>{CSS}</style>

      {/* ── Hero ── */}
      <header className={`sh-hero sh-tone-${overallTone}`}>
        <div className="sh-hero-main">
          <span className="sh-orb">
            <Icon name={overallTone === 'green' ? 'check' : overallTone === 'gray' ? 'info' : 'warn'} size={26} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="sh-hero-kicker">
              System Health{region?.region ? ` · ${region.region}` : ''}
            </div>
            <h1>{overallHeadline}</h1>
            <p className="sh-hero-sub">{snapshotLabel}</p>
          </div>
        </div>

        <div className="sh-hero-actions">
          <span className="sh-auto">Refreshes every {REFRESH_MS / 1000}s</span>
          <button type="button" className="sh-btn" onClick={handleBack}>
            <Icon name="back" size={16} /> Back to Dashboard
          </button>
          <button type="button" className="sh-btn sh-btn--primary" onClick={fetchHealth} disabled={refreshing}>
            <Icon name="refresh" size={16} className={refreshing ? 'sh-spin' : undefined} />
            {refreshing ? 'Refreshing' : 'Refresh now'}
          </button>
        </div>
      </header>

      {/* ── Stale-data notice ── */}
      {errored && data && (
        <div className="sh-banner" role="status">
          <Icon name="warn" size={16} />
          Couldn’t refresh the health data. Showing the last snapshot.
        </div>
      )}

      {/* ── Loading ── */}
      {loading && !data && (
        <>
          <div className="sh-strip" aria-busy="true">
            {[0, 1, 2, 3, 4].map((i) => (
              <div className="sh-kpi" key={i}>
                <div className="sh-skel" style={{ height: 16, width: '45%' }} />
                <div className="sh-skel" style={{ height: 28, width: '70%' }} />
                <div className="sh-skel" style={{ height: 12, width: '55%' }} />
              </div>
            ))}
          </div>
          <div className="sh-card" style={{ padding: 22 }}>
            <div className="sh-skel" style={{ height: 18, width: 180, marginBottom: 18 }} />
            <div className="sh-skel" style={{ height: 120 }} />
          </div>
        </>
      )}

      {/* ── Error (no data yet) ── */}
      {errored && !data && (
        <div className="sh-alert" role="alert" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Icon name="warn" />
            Unable to connect to the health API.
          </span>
          <button type="button" className="sh-btn" onClick={fetchHealth}>Retry</button>
        </div>
      )}

      {/* ── Main view ── */}
      {data && (
        <>
          {/* Overview strip */}
          <div className="sh-strip">
            {kpis.map((k) => (
              <div key={k.key} className={`sh-kpi sh-tone-${getStatusTone(k.status)}`}>
                <div className="sh-kpi-top">
                  <span className="sh-kpi-ico"><Icon name={k.icon} size={16} /></span>
                  {k.label}
                </div>
                <div className="sh-kpi-val">{k.value}</div>
                <div className="sh-kpi-sub">{k.sub}</div>
                <div className="sh-kpi-bar" />
              </div>
            ))}
          </div>

          {/* EC2 */}
          <Section
            icon="server" title="EC2 instances" flush={!isEc2NotConfigured && ec2Instances.length > 0}
            subtitle={`${ec2RunningCount} running, ${ec2TerminatedList.length} terminated`}
            status={ec2?.status || 'unhealthy'}
          >
            {isEc2NotConfigured ? (
              <NotConfigured reason={ec2.reason} />
            ) : ec2Instances.length === 0 ? (
              <div className="sh-empty">No instances returned.</div>
            ) : (
              <>
                <div className="sh-scroll">
                  <table className="sh-table">
                    <thead>
                      <tr>
                        <th>Instance</th><th>Type</th><th>State</th>
                        <th>System check</th><th>Instance check</th><th>CPU</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ec2Instances.map((inst, idx) => {
                        const cpuVal = num(inst?.cpu_utilization_pct ?? inst?.cpu_utilization_percent ?? inst?.cpu);
                        const running = String(inst?.state).toLowerCase() === 'running';
                        return (
                          <tr key={inst?.instance_id || idx}>
                            <td>
                              <div className="sh-name">{inst?.name || inst?.instance_name || '—'}</div>
                              {inst?.instance_id && <div className="sh-id sh-mono">{inst.instance_id}</div>}
                            </td>
                            <td className="sh-mono sh-dim">{inst?.instance_type || inst?.type || '—'}</td>
                            <td><StatusPill status={inst?.state} /></td>
                            <td>
                              <StatusPill status={inst?.system_check || inst?.system_status || (running ? 'passed' : '—')} />
                            </td>
                            <td>
                              <StatusPill status={inst?.instance_check || inst?.instance_status || (running ? 'passed' : '—')} />
                            </td>
                            <td>
                              {cpuVal !== null ? (
                                <Bar value={cpuVal} tone={cpuVal > 80 ? 'red' : 'green'} label={`${cpuVal.toFixed(1)}%`} />
                              ) : (
                                <span className="sh-dim">No data</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {ec2TerminatedList.map((inst, idx) => (
                  <div className="sh-note" key={inst?.instance_id || idx}>
                    <Icon name="info" size={15} />
                    <span>
                      <strong>{inst?.name || inst?.instance_id || 'Instance'}</strong> is terminated and not counted as running.
                    </span>
                  </div>
                ))}
              </>
            )}
          </Section>

          {/* Database */}
          <Section
            icon="database" title="Database (Amazon RDS)"
            subtitle={`${rds?.available_databases ?? 0} available of ${rds?.total_databases ?? 0} total`}
            status={rds?.status || 'unhealthy'}
          >
            {isRdsNotConfigured ? (
              <NotConfigured reason={rds.reason} />
            ) : rdsDatabases.length === 0 ? (
              <div className="sh-empty">No databases returned.</div>
            ) : (
              rdsDatabases.map((db, idx) => {
                const alloc = num(db?.allocated_storage_gb);
                const freeStorage = num(db?.free_storage_gb);
                const usedStorage = alloc !== null && freeStorage !== null ? Math.max(0, alloc - freeStorage) : null;
                const usedPct = alloc && usedStorage !== null ? Math.round((usedStorage / alloc) * 100) : null;
                const cpuVal = num(db?.cpu_utilization_pct ?? db?.cpu_utilization_percent ?? db?.cpu);
                const freeMemGb = num(db?.free_memory_mb) !== null ? `${(db.free_memory_mb / 1024).toFixed(1)} GB` : '—';

                return (
                  <div className="sh-db" key={db?.db_identifier || idx}>
                    <div className="sh-db-head">
                      <div className="sh-db-name">
                        {db?.db_identifier || 'Database'}
                        <StatusPill status={db?.status} />
                        {db?.engine && <span className="sh-chip">{db.engine}</span>}
                      </div>
                      {db?.endpoint && (
                        <div className="sh-endpoint">
                          <code className="sh-mono" title={db.endpoint}>{db.endpoint}</code>
                          <CopyButton text={db.endpoint} />
                        </div>
                      )}
                    </div>

                    <div className="sh-tiles">
                      <Tile label="CPU utilization">
                        {cpuVal !== null ? (
                          <>
                            <div className="sh-tile-val">{cpuVal.toFixed(1)}%</div>
                            <Bar wide value={cpuVal} tone={cpuVal > 80 ? 'red' : 'green'} />
                          </>
                        ) : (
                          <div className="sh-dim">No data</div>
                        )}
                      </Tile>

                      <Tile label="Storage used" sub={usedPct !== null ? `${usedStorage.toFixed(1)} of ${alloc} GB` : undefined}>
                        {usedPct !== null ? (
                          <>
                            <div className="sh-tile-val">{usedPct}%</div>
                            <Bar wide value={usedPct} tone={usedPct > 85 ? 'red' : 'green'} />
                          </>
                        ) : (
                          <div className="sh-dim">No data</div>
                        )}
                      </Tile>

                      <Tile label="Connections" value={db?.database_connections ?? db?.connections ?? '—'} sub="Open right now" />

                      <Tile
                        label="Free memory" value={freeMemGb}
                        sub={freeStorage !== null ? `${freeStorage} GB free storage` : undefined}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </Section>

          {/* Region + Priority sync, side by side */}
          <div className="sh-cols">
            <Section icon="globe" title="Region reachability" subtitle={region?.region || 'us-east-1'} status={region?.status || 'healthy'}>
              <div className="sh-tiles">
                <Tile
                  label="Response time"
                  value={num(region?.latency_ms) !== null ? `${Math.round(region.latency_ms).toLocaleString()} ms` : '—'}
                />
                <Tile label="HTTP status" value={region?.http_status ? `HTTP ${region.http_status}` : '—'} />
              </div>
            </Section>

            <Section
              icon="sync" title="Priority sync"
              subtitle="Orders waiting on NetSuite sync and label creation" status={sync?.status || 'healthy'}
            >
              <div className="sh-tiles">
                <Tile
                  label="Waiting to sync"
                  value={num(sync?.not_synced) !== null ? sync.not_synced.toLocaleString() : '—'}
                  sub="Orders queued for NetSuite sync"
                />
                <Tile
                  label="No label"
                  value={num(sync?.no_label) !== null ? sync.no_label.toLocaleString() : '—'}
                  sub="Orders missing shipping label"
                />
              </div>
            </Section>
          </div>

          {/* NetSuite */}
          <Section
            icon="plug" title="NetSuite endpoints"
            subtitle={`${ns?.successful ?? 0} of ${ns?.total ?? 0} succeeded, ${ns?.failed ?? 0} failed`}
            status={ns?.status || 'healthy'}
          >
            {epList.length === 0 ? (
              <div className="sh-empty">No NetSuite endpoints reported.</div>
            ) : (
              <div className="sh-ep-grid">
                {epList.map((ep, idx) => {
                  const isFailed = ep?.is_success === false || ep?.status === 'failed';
                  const sec = num(ep?.execution_time_seconds);
                  const pct = sec !== null ? Math.max(3, Math.min(100, (sec / maxExecTime) * 100)) : 0;
                  return (
                    <div key={ep?.endpoint || idx} className={`sh-ep sh-tone-${isFailed ? 'red' : 'green'}`}>
                      <div className="sh-ep-head">
                        <span className="sh-ep-title sh-mono">{ep?.endpoint || '—'}</span>
                        <StatusPill status={ep?.is_success ? 'success' : (ep?.status || 'failed')} />
                      </div>
                      <div className="sh-ep-metrics">
                        <div>
                          <div className="sh-ep-num">{formatRunTime(sec)}</div>
                          <div className="sh-ep-label">Run time</div>
                        </div>
                        <div>
                          <div className="sh-ep-num">{ep?.http_status ? String(ep.http_status) : '—'}</div>
                          <div className="sh-ep-label">HTTP status</div>
                        </div>
                      </div>
                      <div className="sh-ep-bar">
                        <div className="sh-track sh-track--wide">
                          <div className="sh-fill" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                      <div className="sh-ep-foot">
                        <span>Last updated</span>
                        <strong>{relativeTime(ep?.updated_at)}</strong>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>

          {/* Alarms (only when the API sends them) */}
          {alarms && (
            <Section
              icon="bell" title="Alarms" subtitle="CloudWatch metric alarms"
              status={alarmFiring > 0 ? 'alarm' : 'ok'}
            >
              <div className="sh-tiles">
                <Tile label="OK" value={alarmOk} />
                <Tile label="In alarm" value={alarmFiring} />
                <Tile label="Insufficient data" value={alarmInsufficient} />
              </div>
              {alarmList.length > 0 ? (
                <div className="sh-stack">
                  {alarmList.map((alm, idx) => (
                    <div className="sh-alarm-item" key={idx}>
                      <Icon name="warn" size={16} />
                      {typeof alm === 'string' ? alm : (alm?.name || alm?.alarm_name || 'Alarm triggered')}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="sh-empty" style={{ marginTop: 12 }}>No alarms currently firing.</div>
              )}
            </Section>
          )}
        </>
      )}
    </div>
  );
};

// ─── SystemHealthPage (wrapped in DashboardLayout & Error Boundary) ───────────
const SystemHealthPage = () => (
  <DashboardLayout>
    <SystemHealthErrorBoundary>
      <SystemHealthContent />
    </SystemHealthErrorBoundary>
  </DashboardLayout>
);

export default SystemHealthPage;
